import Clarity from "@microsoft/clarity";

import { ConfigProvider } from "antd";
import "bootstrap/dist/css/bootstrap.min.css";
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, RouterProvider } from "react-router-dom";
import OTPModal from "./components/custom/OTPModal";
import InfoModal from "./components/custom/modal/info/InfoModal";
import AdminWrapper from "./components/main/AdminWrapper";
import Pricingbar from "./components/main/pricingbar";
import { APP_REACT_URL } from "./constants/AppConstants";
import { ProviderObj } from "./context/AppContext";
import { PusherProvider } from "./context/PusherContext";
import { RolesProvider } from "./context/RolesContext";
import { EscHandlerProvider } from "./context/EscHandlerContext";
import "./index.css";
import createRouter from "./pages/router";
import { CommonLoadingV2 } from "./pages/utils/CommonVessels";
import PermissionDeniedModal from "./pages/utils/PermissionDeniedModal";
import SurveyWrapper from "./survey/Survey";
import { VoiceProvider } from "./voice/context/VoiceContext";
import BackgroundTaskWrapper from "./components/main/BackgroundTaskWrapper";
import antdConfig from "./antdConfig";
import HNotification from "./components/custom/notification/HNotification";
import { BeamsProvider } from "./context/BeamsContext";
import useTabTitle from "./hooks/useTabTitle";

const searchParams = new URLSearchParams(window.location.search);
const isInternal = searchParams.get("internal") === "true";
const pathNameArray = window.location.pathname.replace(/^\/+/, "").split("/");
const isAdminTool = pathNameArray?.[0] === "admin";
const stopThirdParty = isInternal || isAdminTool || pathNameArray.includes("sendsms") || pathNameArray.includes("dialpad");

const App = () => {
  let pathName = window.location.pathname;
  const pathSegments = pathName.split("/");
  const [pricing] = useState(pathSegments?.[1] === "pricing" || pathSegments?.[1] === "package");
  const [permissionDeniedToSubAccount, setPermissionDeniedToSubAccount] = useState(false);
  const [admin] = useState(pathSegments?.indexOf("admin") !== -1 ? true : false);
  const [survey] = useState(pathSegments?.indexOf("survey") !== -1 ? true : false);
  const [isBackgroundTask] = useState(pathSegments?.indexOf("background-task") !== -1 ? true : false);

  const [otpModalState, setOtpModalState] = useState(false);
  const [showCaptchaWidget, setShowCaptchaWidget] = useState(false);
  const [blockInteractionModalProps, setBlockInteractionModalProps] = useState({
    props: {},
  });
  const [accessDenied, setAccessDenied] = useState(false);
  const [appPropsObject, setAppPropsObject] = useState({});
  const [loading, setLoading] = useState(
    pathSegments?.indexOf("content") !== -1 ? false : true
  );

  const loadingContextValue = {
    setLoading: setLoading,
    loading: loading,
  };

  // Update tab title with notification count
  useTabTitle();

  useEffect(() => {
    if (!stopThirdParty && process.env.REACT_APP_ENV !== "development") {
      // Defer third-party scripts loading until after page load
      const loadThirdPartyScripts = () => {
        const projectId = process.env.REACT_APP_ENV === "production-1" ? "nd80pjlpbf" : "pygp7sgnfn";
        Clarity.init(projectId);

        const pagesenseScript = document.createElement("script");
        pagesenseScript.src = "https://cdn.pagesense.io/js/deskoappsxyz/ffc2aa6d77c8431d8479ddae3f8efc28.js";
        pagesenseScript.async = true;
        document.body.appendChild(pagesenseScript);

        window.$sleek = [];
        window.SLEEK_PRODUCT_ID = process.env.REACT_APP_SLEEKPLAN_PRODUCT_ID;
        const sleekPlanScript = document?.createElement("script");
        sleekPlanScript.src = "https://client.sleekplan.com/sdk/e.js";
        sleekPlanScript.async = true;
        document?.body?.appendChild(sleekPlanScript);

        var hasSideBarFalse = searchParams.get("sideBar") === "false";
        var isLandingPage = pathName.includes("/landing");

        var containsPreviousPath = searchParams.get("prevPath") !== null;

        // Disable if path is in disabled list OR (sideBar=false AND not a landing page) OR isInternalPage
        var isTidioDisabled = (hasSideBarFalse && (!isLandingPage || containsPreviousPath)) || navigator.userAgent.includes("HelloSendMobile");

        if (!isTidioDisabled) {
          const tidioScript = document?.createElement("script");
          tidioScript.src = "https://code.tidio.co/gycvgq9nlsxmzsqyquclxsmfbloiyfao.js";
          tidioScript.async = true;
          document?.body?.appendChild(tidioScript);
        }


        const tidioChatWidgetElement = document.getElementById("tidio-chat");
        const tidioChatWidgetShadowRoot = tidioChatWidgetElement?.shadowRoot;

        const style = document.createElement("style");
        style.textContent = `
             #button.sidebar {
                 bottom: calc(25% - 100px) !important;
              } `;

        if (tidioChatWidgetShadowRoot) {
          tidioChatWidgetShadowRoot.appendChild(style);
        }

        return () => {
          const existingSleekScript = document.querySelector('script[src*="sleekplan.com"]');
          const existingPagesenseScript = document.querySelector('script[src*="pagesense.io"]');
          if (existingSleekScript) document.body.removeChild(existingSleekScript);
          if (existingPagesenseScript) document.body.removeChild(existingPagesenseScript);
        };
      };

      // Use requestIdleCallback for better performance, fallback to setTimeout
      if (window.requestIdleCallback) {
        window.requestIdleCallback(loadThirdPartyScripts, { timeout: 2000 });
      } else {
        // Fallback: Load after a short delay to ensure page has rendered
        setTimeout(loadThirdPartyScripts, 2000);
      }

      // Also load after window load event as additional safety
      if (document.readyState === "complete") {
        loadThirdPartyScripts();
      } else {
        window.addEventListener("load", loadThirdPartyScripts, { once: true });
      }

      return () => {
        const existingSleekScript = document.querySelector('script[src*="sleekplan.com"]');
        const existingPagesenseScript = document.querySelector('script[src*="pagesense.io"]');
        if (existingSleekScript) document.body.removeChild(existingSleekScript);
        if (existingPagesenseScript) document.body.removeChild(existingPagesenseScript);
      };
    }
  }, []);
  const router = createRouter(loadingContextValue, null, accessDenied, setAccessDenied);
  return (
    <>
      {pricing ? (
        <Router>
          <Pricingbar setOtpModalState={setOtpModalState} otpModalState={otpModalState} />
        </Router>
      ) : admin ? ( // Router><Admin/></Router>
        (<AdminWrapper />)
      ) : survey ? (
        <SurveyWrapper />
      ) : isBackgroundTask ? (
        <BackgroundTaskWrapper />
      ) : (
        <>
          <ConfigProvider theme={antdConfig}>
            {/* {notificationContextHolder} */}
            <HNotification />
            {otpModalState && (
              <OTPModal
                appPropsObject={appPropsObject}
                open={otpModalState}
                setLoading={setLoading}
                setOtpModalState={setOtpModalState}
                showCaptchaWidget={showCaptchaWidget}
                setShowCaptchaWidget={setShowCaptchaWidget}
              />
            )}
            <PermissionDeniedModal permissionDeniedToSubAccount={permissionDeniedToSubAccount} />
            <EscHandlerProvider>
              <RolesProvider>
                <ProviderObj
                  setLoading={setLoading}
                  setBlockInteractionModalProps={setBlockInteractionModalProps}
                  setOtpModalState={setOtpModalState}
                  setPermissionDeniedToSubAccount={setPermissionDeniedToSubAccount}
                  setAppPropsObject={setAppPropsObject}
                  setAccessDenied={setAccessDenied}
                  accessDenied={accessDenied}
                  setShowCaptchaWidget={setShowCaptchaWidget}
                  showCaptchaWidget={showCaptchaWidget}
                >
                  {/* <Router> */}
                  {loading ? <CommonLoadingV2 /> : null}
                  {/* <Space
                  direction="vertical"
                  style={{ width: "100%", background: "none" }}
                  size={[0, 48]}
                  > */}

                  {/* </Space> */}
                  {/* </Router> */}
                  <BeamsProvider>
                  <PusherProvider>
                    <VoiceProvider>
                      <RouterProvider router={router} />
                    </VoiceProvider>
                  </PusherProvider>
                  </BeamsProvider>
                  {blockInteractionModalProps?.modalKey && (
                    <BlockInteractionModalByKey modalKey={blockInteractionModalProps.modalKey} props={blockInteractionModalProps.props} />
                  )}
                </ProviderObj>
              </RolesProvider>
            </EscHandlerProvider>
          </ConfigProvider>
        </>
      )}
    </>
  );
};

function BlockInteractionModalByKey({ modalKey, props }) {
  if (modalKey.includes("campaign-success")) {
    return (
      <InfoModal
        content={props?.message}
        title={props?.title}
        modalClassName="modal-success"
        type="success"
        open={true}
        okButtonText={props?.buttonText}
        handleOk={() => {
          window.open(APP_REACT_URL + props?.buttonHref, "_blank");
        }}
      />
    );
  }
  return null;
}

export default App;
